import { ReactNode, useEffect, useRef, useState } from 'react';

import { ChevronDown } from 'lucide-react';

export type LeftNavSectionProps = {
  /** Section content (typically LeftNavItem components) */
  children: ReactNode;
  /** Optional section header label (hidden in collapsed mode) */
  label?: string;
  /** Additional CSS classes applied to the section container */
  className?: string;
  /** Enable collapsible behavior for the section */
  collapsible?: boolean;
  /** Default collapsed state (only applies when collapsible is true) */
  defaultCollapsed?: boolean;
  /** Unique identifier for localStorage persistence. If not provided, label is used. */
  id?: string;
};

/**
 * LeftNavSection - Section grouping for LeftNavLayout navigation items
 *
 * Groups related navigation items together with an optional label.
 * In collapsed mode, the label is hidden but the grouping is preserved.
 * Supports collapsible behavior with smooth animations and localStorage persistence.
 *
 * @example
 * ```tsx
 * <LeftNavSection label="Main">
 *   <LeftNavItem icon={<HomeIcon />} href="/">Home</LeftNavItem>
 *   <LeftNavItem icon={<DashboardIcon />} href="/dashboard">Dashboard</LeftNavItem>
 * </LeftNavSection>
 *
 * <LeftNavSection label="Settings" collapsible defaultCollapsed id="settings-section">
 *   <LeftNavItem icon={<SettingsIcon />} href="/settings">Settings</LeftNavItem>
 * </LeftNavSection>
 * ```
 */
function LeftNavSection({
  children,
  label,
  className = '',
  collapsible = false,
  defaultCollapsed = false,
  id,
}: Readonly<LeftNavSectionProps>) {
  const STORAGE_KEY = 'left-nav-sections';

  // Generate unique identifier from id or label
  const sectionId = id ?? label ?? null;

  // Helper to read all section states from localStorage
  const getSectionStates = (): Record<string, boolean> => {
    if (typeof window === 'undefined') return {};

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.warn('Failed to read section states from localStorage:', error);
      return {};
    }
  };

  // Helper to save a section state to localStorage
  const saveSectionState = (sectionKey: string, collapsed: boolean) => {
    try {
      const states = getSectionStates();
      states[sectionKey] = collapsed;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(states));
    } catch (error) {
      console.warn('Failed to save section state to localStorage:', error);
    }
  };

  // Initialize collapsed state from localStorage if collapsible
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (!collapsible || !sectionId) return defaultCollapsed;

    const states = getSectionStates();
    return sectionId in states ? states[sectionId] : defaultCollapsed;
  });

  const [isNavCollapsed, setIsNavCollapsed] = useState(false);
  const prevNavCollapsedRef = useRef(false);
  const [animate, setAnimate] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  // Check if parent nav is collapsed (icon-only mode)
  useEffect(() => {
    const checkNavCollapsed = () => {
      const navElement = sectionRef.current?.closest('nav');
      if (navElement) {
        const nowCollapsed =
          navElement.getAttribute('data-pre-collapsed') === 'true';
        // When nav transitions from icon-mode back to expanded, instantly restore
        // the section's collapsed state instead of animating it closed.
        if (prevNavCollapsedRef.current && !nowCollapsed) {
          setAnimate(false);
          requestAnimationFrame(() => setAnimate(true));
        }
        prevNavCollapsedRef.current = nowCollapsed;
        setIsNavCollapsed(nowCollapsed);
      }
    };

    checkNavCollapsed();

    const navElement = sectionRef.current?.closest('nav');
    if (navElement) {
      const observer = new MutationObserver(checkNavCollapsed);
      observer.observe(navElement, {
        attributes: true,
        attributeFilter: ['data-pre-collapsed'],
      });
      return () => observer.disconnect();
    }

    return undefined;
  }, []);

  const toggleCollapsed = () => {
    if (collapsible) {
      const newState = !isCollapsed;
      setIsCollapsed(newState);

      // Persist to localStorage
      if (sectionId) {
        saveSectionState(sectionId, newState);
      }
    }
  };

  // Auto-expand when nav is in icon-only mode
  const effectiveCollapsed = isNavCollapsed ? false : isCollapsed;

  return (
    <div
      ref={sectionRef}
      className={`py-1 [&:last-child>*:last-child]:hidden ${className}`}
    >
      {/* Section Label - Hidden in collapsed mode */}
      {label && (
        <div
          className={`text-sidebar-foreground/60 mt-1 mb-1.5 px-3 text-xs font-semibold tracking-wider uppercase [nav[data-pre-collapsed='true']_&]:hidden ${collapsible ? 'hover:text-sidebar-foreground flex cursor-pointer items-center justify-between gap-2 transition-colors' : ''} `}
          onClick={toggleCollapsed}
          role={collapsible ? 'button' : undefined}
          aria-expanded={collapsible ? !effectiveCollapsed : undefined}
          tabIndex={collapsible ? 0 : undefined}
          onKeyDown={
            collapsible
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleCollapsed();
                  }
                }
              : undefined
          }
        >
          <span>{label}</span>
          {collapsible && (
            <ChevronDown
              size={16}
              className={`transition-transform duration-200 ${effectiveCollapsed ? '-rotate-90' : ''}`}
            />
          )}
        </div>
      )}

      {/* Section Items */}
      <div
        ref={contentRef}
        className={`space-y-0.5 overflow-hidden ${animate ? 'transition-all duration-200' : ''} ${effectiveCollapsed && collapsible ? 'max-h-0 opacity-0' : 'max-h-500 opacity-100'}`}
      >
        {children}
      </div>

      {/* Divider - Subtle separator between sections (hidden on last section) */}
      <div className="bg-sidebar-border mx-3 mt-1.5 h-px" aria-hidden="true" />
    </div>
  );
}

LeftNavSection.displayName = 'LeftNavSection';

export default LeftNavSection;
